import { Participant, SessionGroup, SplitType, peaksFor } from '../../invitations/models/invitation.model';

/**
 * Placeholder split algorithms. The backend will own the real ones; this frontend will render
 * its response. Fidelity to the eventual server behaviour is explicitly not a goal — plausible,
 * well-shaped output is. This file is the swap point: when the endpoints arrive, delete it and
 * change the call site in InvitationMockService. Nothing else moves.
 *
 * Pure by design: no Angular, no service, no storage, no Date.now(), no Math.random().
 */

const group = (i: number, memberIds: string[] = []): SessionGroup =>
  ({ id: `g-${i + 1}`, name: `Group ${i + 1}`, memberIds });

/** Stable key for a peak set: 'cl+id'. Integrator (no peaks) gets its own bucket. */
const peakKey = (p: Participant): string => {
  const peaks = peaksFor(p.profileTypeId);
  return peaks.length ? peaks.join('+') : 'balanced';
};

/** Bucket by peak set, in first-appearance order so output is stable. */
function byPeakSet(members: Participant[]): Participant[][] {
  const buckets = new Map<string, Participant[]>();
  members.forEach(m => {
    const k = peakKey(m);
    (buckets.get(k) ?? buckets.set(k, []).get(k)!).push(m);
  });
  return [...buckets.values()];
}

/**
 * Similar — people who share a peak set sit together, packed into the `count` groups the caller
 * asked for. An Early Bird sits with Early Birds rather than being filed under Clarify.
 *
 * This used to return one group per distinct profile type present and ignore `count` entirely,
 * which meant asking for 4 groups could produce 15 — there are 15 FourSight types. Whole buckets
 * now go into the emptiest group, which keeps each peak set intact while evening out sizes.
 *
 * The real algorithm is the backend's (see the API spec); this is the placeholder that has to be
 * predictable, not clever.
 */
export function splitSimilar(members: Participant[], count: number): SessionGroup[] {
  const n = Math.max(1, Math.floor(count));
  // Largest bucket first: placing the big clusters while every bin is still empty is what keeps
  // the final sizes close together.
  const buckets = byPeakSet(members).sort((a, b) => b.length - a.length);
  const bins: Participant[][] = Array.from({ length: n }, () => []);

  buckets.forEach(bucket => {
    let smallest = 0;
    bins.forEach((bin, i) => { if (bin.length < bins[smallest].length) { smallest = i; } });
    bins[smallest].push(...bucket);
  });

  // More groups requested than distinct peak sets: a bucket has to be broken up, or the caller
  // gets fewer groups than they asked for. Take from the largest bin so the break lands on the
  // biggest cluster, where it costs the least similarity.
  for (let empty = bins.findIndex(b => !b.length); empty !== -1; empty = bins.findIndex(b => !b.length)) {
    let largest = 0;
    bins.forEach((bin, i) => { if (bin.length > bins[largest].length) { largest = i; } });
    if (bins[largest].length < 2) { break; }   // fewer members than groups — empties are unavoidable
    bins[empty].push(bins[largest].pop()!);
  }

  return bins.map((b, i) => group(i, b.map(m => m.id))).filter(g => g.memberIds.length > 0);
}

/**
 * Deal from the peak buckets across `count` groups, snaking each pass, so each group spans
 * preferences. Deterministic: same input, same output.
 */
function deal(buckets: Participant[][], count: number): SessionGroup[] {
  const n = Math.max(1, Math.floor(count));
  const groups: string[][] = Array.from({ length: n }, () => []);
  // Largest bucket first, so the dominant preference is spread before the rare ones land.
  const ordered = [...buckets].sort((a, b) => b.length - a.length).flat();
  ordered.forEach((m, i) => {
    const pass = Math.floor(i / n);
    const slot = i % n;
    groups[pass % 2 === 0 ? slot : n - 1 - slot].push(m.id);   // snake
  });
  return groups.map((ids, i) => group(i, ids)).filter(g => g.memberIds.length > 0);
}

export function splitDiverseExact(members: Participant[], count: number): SessionGroup[] {
  return deal(byPeakSet(members), count);
}

/**
 * Both remaining splits are deterministic, so there is no `seed` parameter: the same members and
 * count always produce the same groups. That is what lets the wizard's preview be a promise rather
 * than a sample — it previously differed from the saved result for the lottery split, which was
 * the only randomised one.
 */
export function runSplit(members: Participant[], splitType: SplitType, count: number): SessionGroup[] {
  switch (splitType) {
    case 'similar': return splitSimilar(members, count);
    case 'diverse_exact': return splitDiverseExact(members, count);
  }
}
